import type { SharedSliceContent } from "@prismicio/types-internal/lib/content"
import { useReducer, useRef } from "react"

import { refineSlice } from "../../../../app/services/ai"
import { useRepository } from "../../../../app/services/repository"
import {
  trackRefineSliceDone,
  trackRefineSliceStarted
} from "../../../../app/services/tracking"
import { useDocument } from "../../../DocumentProvider"
import type { VisualSharedSlice } from "../sliceTableData"

export interface UseRefineSliceResult {
  status: "idle" | "refining" | "error"
  prompt: string
  versions: Version[]
  selected: number
  selectedVersion?: SelectedVersion
  disabled: boolean
  simulatorUrl?: string
  init: (args: { promptId?: string; slice: VisualSharedSlice }) => void
  refine: () => void
  clear: () => void
  setPrompt: (newPrompt: string) => void
  setSelected: (newSelected: number) => void
}

export function useRefineSlice(): UseRefineSliceResult {
  const {
    documentId,
    locale,
    data: documentData,
    draft: { document }
  } = useDocument()

  const { simulatorUrl } = useRepository()

  const [
    { initialSlice, prompt: rawPrompt, selected, status, versions },
    dispatch
  ] = useReducer(reducer, initialState)

  const prompt = rawPrompt.trim()

  const disabled = status === "refining" || !prompt || !initialSlice

  const promptIdRef = useRef<string>()

  function refine() {
    if (disabled) return

    const { content } = versions[selected] ?? {}
    if (!initialSlice || !content) return

    promptIdRef.current = undefined

    dispatch({ type: "refineStarted" })

    const promptId = crypto.randomUUID()
    const { variation } = initialSlice
    const started = Date.now()

    promptIdRef.current = promptId

    const trackingInfo = {
      ...(documentId && { documentId }),
      customTypeId: documentData.customType.id,
      prompt,
      promptId,
      locale
    }

    trackRefineSliceStarted(trackingInfo)

    refineSlice({
      uuid: promptId,
      document,
      variation,
      content,
      prompt,
      locale,
      onMessage: (message) => {
        // Ignore messages if the prompt is not relevant anymore.
        if (promptId !== promptIdRef.current) return

        switch (message.status) {
          case "refining":
            break
          case "done":
            trackRefineSliceDone({
              ...trackingInfo,
              cost: String(message.cost),
              time: ((Date.now() - started) / 1000).toFixed(1)
            })
            dispatch({
              type: "refineSucceeded",
              promptId,
              slice: message.slice
            })
            break
          case "failed":
            dispatch({ type: "refineFailed" })
            break
        }
      }
    })
  }

  function init(args: { promptId?: string; slice: VisualSharedSlice }) {
    promptIdRef.current = undefined
    dispatch({ type: "init", ...args })
  }

  function clear() {
    dispatch({ type: "cleared" })
  }

  function setPrompt(newPrompt: string) {
    dispatch({ type: "promptChanged", prompt: newPrompt })
  }

  function setSelected(newSelected: number) {
    dispatch({ type: "selectedChanged", selected: newSelected })
  }

  const { promptId, content } = versions[selected] ?? {}
  const selectedVersion = initialSlice &&
    content && {
      promptId,
      slice: {
        ...initialSlice,
        content
      }
    }

  return {
    status,
    prompt: rawPrompt,
    setPrompt,
    versions,
    selected,
    setSelected,
    selectedVersion,
    disabled,
    simulatorUrl,
    init,
    refine,
    clear
  }
}

interface ReducerState {
  prompt: string
  selected: number
  status: "idle" | "refining" | "error"
  initialSlice?: VisualSharedSlice
  versions: Version[]
}

interface Version {
  promptId?: string
  content: SharedSliceContent
}

interface SelectedVersion {
  promptId?: string
  slice: VisualSharedSlice
}

const initialState: ReducerState = {
  prompt: "",
  selected: 0,
  status: "idle",
  initialSlice: undefined,
  versions: []
}

type ReducerEvent =
  | {
      type: "init"
      promptId?: string
      slice: VisualSharedSlice
    }
  | {
      type: "promptChanged"
      prompt: string
    }
  | {
      type: "selectedChanged"
      selected: number
    }
  | {
      type: "refineStarted"
    }
  | {
      type: "refineSucceeded"
      promptId: string
      slice: SharedSliceContent
    }
  | {
      type: "refineFailed"
    }
  | {
      type: "cleared"
    }

function reducer(state: ReducerState, event: ReducerEvent): ReducerState {
  switch (event.type) {
    case "init": {
      const initialSlice = event.slice
      return {
        ...initialState,
        initialSlice,
        versions: [
          {
            promptId: event.promptId,
            content: initialSlice.content
          }
        ]
      }
    }
    case "promptChanged":
      return {
        ...state,
        prompt: event.prompt,
        status: "idle"
      }
    case "selectedChanged":
      return {
        ...state,
        selected: event.selected
      }
    case "refineStarted":
      return {
        ...state,
        status: "refining"
      }
    case "refineSucceeded": {
      const versions = [
        ...state.versions,
        {
          promptId: event.promptId,
          content: event.slice
        }
      ]
      const selected = versions.length - 1
      return {
        ...state,
        prompt: "",
        selected,
        status: "idle",
        versions
      }
    }
    case "refineFailed":
      return {
        ...state,
        status: "error"
      }
    case "cleared":
      return initialState
  }
}
